/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import {
    Badge,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Box,
    Text,
    VStack,
    HStack,
} from '@chakra-ui/react';
import { faInfoCircle } from '@fortawesome/sharp-light-svg-icons';
import { format, isPast } from 'date-fns';
import { TenancyDetail } from '@acp/shared';
import { FaIcon } from '../fa-icon';
import { OccupantContacts } from './occupant-contacts';

function OccupantSummaryNames({
    name,
    remainingCount,
}: {
    name: string;
    remainingCount: number;
}) {
    if (name === '') return null;

    return (
        <HStack align="start">
            <FaIcon icon={faInfoCircle} color="muted.500" pt={1} />
            <VStack align="left" gap={0}>
                <Text noOfLines={1} pr={1}>{name}</Text>
                <Text color="muted.500" fontSize="sm">
                    {remainingCount > 0 ? `& ${remainingCount} more` : null}
                </Text>
            </VStack>
        </HStack>
    );
}

function VacantInfo({
    isVacant,
    vacateDate,
    untilDate,
}: {
    isVacant: boolean | null | undefined;
    vacateDate: string | null | undefined;
    untilDate: string | null | undefined;
}) {
    if (!isVacant) {
        let vacateDateText = '';
        if (vacateDate) {
            vacateDateText = isPast(new Date(vacateDate))
                ? `Vacated on ${format(vacateDate, 'dd/MM/yyyy')}`
                : `Vacating on ${format(vacateDate, 'dd/MM/yyyy')}`;
        }

        return (
            <Text
                fontSize="xs"
                fontWeight="bold"
                cursor="default"
                color="muted.500"
            >
                {vacateDateText}
            </Text>
        );
    }

    return (
        <Box>
            <Badge colorScheme="blackAlpha" cursor="pointer" userSelect="none">
                Vacant
            </Badge>
            {untilDate && (
                <Text
                    fontSize="xs"
                    fontWeight="bold"
                    cursor="default"
                    color="muted.500"
                >
                    Vacant until {format(untilDate, 'dd/MM/yyyy')}
                </Text>
            )}
        </Box>
    );
}

export function OccupantDetails({ data }: { data: TenancyDetail }) {
    const {
        currentTenancy: {
            startDate: tenancyStartDate,
            endDate: tenancyEndDate,
            vacateDate,
            occupants,
        },
        isVacant,
        upcomingTenancy: { startDate: vacantUntilDate },
    } = data || {};

    const firstOccupantName = occupants?.[0]?.name || '';
    const remainingOccupantCount = occupants?.length
        ? Math.max(occupants.length - 1, 0)
        : 0;

    return (
        <>
            {firstOccupantName && (
                <Popover trigger="hover" isLazy>
                    <PopoverTrigger>
                        <Box maxW="fit-content">
                            <OccupantSummaryNames
                                name={firstOccupantName}
                                remainingCount={remainingOccupantCount}
                            />
                        </Box>
                    </PopoverTrigger>
                    <PopoverContent>
                        <PopoverArrow />
                        <PopoverBody>
                            <VStack align="left" gap={2}>
                                <Box>
                                    <Text fontSize="small" fontWeight="bold">
                                        Tenancy
                                    </Text>
                                    <Text fontSize="small">
                                        {tenancyStartDate ? `From ${format(
                                            tenancyStartDate || '',
                                            'dd/MM/yyyy'
                                        )}` : ''}
                                        {tenancyEndDate
                                            ? ` to ${format(
                                                tenancyEndDate,
                                                'dd/MM/yyyy'
                                            )}`
                                            : ''}
                                    </Text>
                                </Box>
                                <OccupantContacts occupants={occupants} />
                            </VStack>
                        </PopoverBody>
                    </PopoverContent>
                </Popover>
            )}
            <VacantInfo
                isVacant={isVacant}
                vacateDate={vacateDate || ''}
                untilDate={vacantUntilDate}
            />
        </>
    );
}
